import styled from '../themes/styled'
import { ToastContainerProps, ToastContainer as ToastContainerBase } from 'react-toastify'
import { Theme } from 'particles/themes'
import { AlertContentType } from './index'

type ContentColorType = 'icon' | 'text'

export const getAlertContentColor = (
    contentColorType: ContentColorType,
    contentType: AlertContentType,
    theme: Theme,
) => {
    if (contentType === 'positive') {
        return theme.colors[contentColorType].positive
    } else if (contentType === 'warning') {
        return theme.colors[contentColorType].warning
    } else if (contentType === 'error') {
        return theme.colors[contentColorType].error
    } else {
        // info
        return theme.colors[contentColorType].info
    }
}

const getAlertIndicatorColor = (contentType: AlertContentType, theme: Theme) => {
    if (contentType === 'positive') {
        return theme.colors.indicator.doneGreen
    } else if (contentType === 'warning') {
        return theme.colors.indicator.darkOrange
    } else if (contentType === 'error') {
        return theme.colors.indicator.ribbonRed
    } else {
        // info
        return theme.colors.indicator.skyBlue
    }
}

export const getAlertBackgroundColor = (contentType: AlertContentType, theme: Theme) => {
    if (contentType === 'positive') {
        return theme.colors.surface.positivePastel
    } else if (contentType === 'warning') {
        return theme.colors.surface.warningPastel
    } else if (contentType === 'error') {
        return theme.colors.surface.errorPastel
    } else {
        // info
        return theme.colors.surface.infoPastel
    }
}

interface StyledAlertProps {
    contentType: AlertContentType
}

export const StyledBannerAlert = styled.div<StyledAlertProps>`
    font-family: inherit;
    border-radius: 4px;
    border-left: ${({ contentType, theme }): string => `6px solid ${getAlertIndicatorColor(contentType, theme)}`};
    padding: ${({ theme }) => theme.spacing[300]};
    position: relative;
    background: ${({ contentType, theme }): string => getAlertBackgroundColor(contentType, theme)};
    .text-container {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[200]};
        width: calc(100% - 40px);
    }
    .close-button {
        position: absolute;
        right: 12px;
        top: 12px;
        z-index: 1;
    }
`

export const StyledButtonContainer = styled.div`
    align-items: center;
    margin-left: auto;
    display: flex;
    gap: ${({ theme }) => theme.spacing[300]};
`

export const StyledComfirmationAlert = styled.div<StyledAlertProps>`
    font-family: inherit;
    display: flex;
    gap: ${({ theme }) => theme.spacing[300]};
    color: ${({ contentType, theme }) => getAlertContentColor('icon', contentType, theme)};
    .icon-content-container {
        display: flex;
        align-items: center;
        gap: ${({ theme }) => theme.spacing[300]};
    }
    .icon-container {
        width: 40px;
        height: 40px;
    }
    .title {
        color: ${({ contentType, theme }) => getAlertContentColor('text', contentType, theme)};
    }
    .text-container {
        flex: 1;
    }
`

export const StyledButton = styled.button<StyledAlertProps>`
    // Border and outline variables
    min-width: 100px;
    height: 32px;
    --border-setting: 1.5px solid;
    --outline-setting: 3px solid;
    // Common button style
    border: 0;
    justify-content: center;
    align-items: center;
    border-radius: 40px;
    box-sizing: border-box;
    white-space: nowrap;
    // Remove focus and use focus-visible instead
    &:focus {
        outline: 0;
    }
    // Background
    ${({ contentType, theme }) => ({
        background: 'none',
        border: `var(--border-setting) ${getAlertIndicatorColor(contentType, theme)}`,
    })}
    ${({ contentType, theme }) => ({
        color: getAlertContentColor('text', contentType, theme),
    })}
`

export const ToastContainer = styled(ToastContainerBase)<ToastContainerProps>(({ theme }) => ({
    width: '68vw',
    maxWidth: '980px',
    minWidth: '600px',
    '@media only screen and (max-width: 768px)': {
        '&.Toastify__toast-container--bottom-center': {
            width: '100vw',
            bottom: 0,
            padding: '0 16px',

            '.Toastify__toast': {
                borderRadius: '12px',
                marginBottom: '16px',
                padding: '16px 24px 26px 24px',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'end',

                '> .Toastify__toast-body': {
                    flex: '1 0 100%',
                },
            },
        },
    },

    '.Toastify__toast': {
        alignItems: 'center',
        borderRadius: '12px',
        marginBottom: '16px',
        padding: '16px 24px 26px 24px',
    },

    '.Toastify__progress-bar': {
        height: '10px',
    },

    '.toast__error': {
        background: theme.colors.surface.errorPastel,
    },

    '.toast__positive': {
        background: theme.colors.surface.positivePastel,
    },

    '.toast__warning': {
        background: theme.colors.surface.warningPastel,
    },

    '.toast__info': {
        background: theme.colors.surface.infoPastel,
    },

    '.progressBar__error': {
        background: theme.colors.indicator.ribbonRed,
    },

    '.progressBar__positive': {
        background: theme.colors.indicator.doneGreen,
    },

    '.progressBar__warning': {
        background: theme.colors.indicator.darkOrange,
    },

    '.progressBar__info': {
        background: theme.colors.indicator.skyBlue,
    },
}))

interface StyledReminderAlertProps {
    form: AlertContentType
    width?: string
}

export const StyledReminderAlert = styled.div<StyledReminderAlertProps>`
    display: flex;
    width: ${({ width }) => width};
    box-sizing: border-box;
    border-radius: 8px;
    padding: 12px;
    gap: ${({ theme }) => theme.spacing[300]};
    background: ${({ form, theme }) => getAlertBackgroundColor(form, theme)};
    .icon {
        margin-top: 2px;
        flex-shrink: 0;
    }
    .content-container {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[100]};
        flex: auto;
    }
    .title-container {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: ${({ theme }) => theme.spacing[100]};
    }
    .close-button {
        margin-top: 2px;
    }
    .link-button {
        justify-content: flex-start;
        text-align: left;
        .button-label {
            cursor: pointer;
        }
    }
`

export const SnackbarContainer = styled(ToastContainerBase)<ToastContainerProps>`
    width: fit-content;
    max-width: 700px;
    min-width: 200px;

    @media only screen and (max-width: 700px) {
        &.Toastify__toast-container--bottom-center {
            width: fit-content;
            max-width: 100vw;
            bottom: 0;
            padding: 0px;
        }

        .Toastify__toast {
            margin: 0px 16px 36px 16px;
        }
    }

    // override default toast style
    @media only screen and (max-width: 480px) {
        &.Toastify__toast-container--bottom-center {
            bottom: 0;
            left: 50%;
            transform: translateX(-50%);
        }
    }

    .Toastify__toast {
        align-items: center;
        border-radius: 12px;
        margin-bottom: 36px;
        padding: 8px 16px;
        min-height: 44px;
    }

    .Toastify__toast-container--bottom-center {
        bottom: 0;
    }

    .toast__error {
        background: ${({ theme }) => theme.colors.surface.errorPastel};
    }

    .toast__positive {
        background: ${({ theme }) => theme.colors.surface.positivePastel};
    }
`

interface StyledPr {
    wrap: boolean
}

export const StyledSnackbarContainer = styled.div<StyledReminderAlertProps & StyledPr>`
    width: ${({ width }) => width};
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[600]};
    border-radius: 12px;
    box-sizing: border-box;

    @media only screen and (max-width: 700px) {
        max-width: calc(100vw - 64px);
    }

    .text-container {
        flex: 1;
        width: 100%;

        .text-component {
            white-space: ${({ wrap }) => (wrap ? 'normal' : 'nowrap')};
        }
    }

    .icon-content-container {
        display: flex;
        gap: ${({ theme }) => theme.spacing[300]};
        align-items: center;
        flex: 1;

        .icon {
            margin-top: 2px;
            flex-shrink: 0;
        }
    }
`
